import { Controller } from "@hotwired/stimulus"
import * as bootstrap from "bootstrap"

// Handles manual subject matching between C3 (unmatched athletes) and Smartabase users.
//
// Workflow:
//   1. Admin clicks "Match" on an unmatched athlete row → modal opens, Smartabase user
//      list is fetched from the server proxy endpoint.
//   2. Admin searches/filters the list by last name, first name, or DOB.
//   3. Admin clicks "Select" on a Smartabase user → confirmation panel appears.
//   4. Admin clicks "Confirm Match" → PATCH request saves external_id on the athlete.
//   5. The matched row is removed from the unmatched table; a success toast appears.

export default class extends Controller {
  static targets = [
    "unmatchedRows",
    "unmatchedCount",
    "modalAthleteLabel",
    "athleteLastName",
    "athleteFirstName",
    "athleteDob",
    "spinner",
    "resultsWrapper",
    "results",
    "resultCount",
    "search",
    "toastBody",
  ]

  static values = {
    fetchUrl:  String,  // GET  /smartabase_integrations/:id/fetch_users
    assignUrl: String,  // PATCH /smartabase_integrations/:id/assign_athlete
  }

  connect() {
    this.allUsers      = []
    this.selectedAthlete = null   // { id, name, firstName, lastName, dob }
    this.pendingUser     = null   // { userId, firstName, lastName, dob }
    this._modal          = null
    this._confirmRow     = null
  }

  // ── Open modal ────────────────────────────────────────────────────────────

  openModal(event) {
    const btn = event.currentTarget
    this.selectedAthlete = {
      id:        btn.dataset.athleteId,
      name:      btn.dataset.athleteName,
      firstName: btn.dataset.athleteFirstName || "",
      lastName:  btn.dataset.athleteLastName || "",
      dob:       btn.dataset.athleteDob || "",
    }

    // Reset modal state
    this.modalAthleteLabelTarget.textContent = this.selectedAthlete.name
    if (this.hasAthleteLastNameTarget) this.athleteLastNameTarget.textContent = this.selectedAthlete.lastName || "--"
    if (this.hasAthleteFirstNameTarget) this.athleteFirstNameTarget.textContent = this.selectedAthlete.firstName || "--"
    if (this.hasAthleteDobTarget) this.athleteDobTarget.textContent = this.selectedAthlete.dob || "--"
    this.searchTarget.value = ""
    this.resultsTarget.innerHTML = ""
    this.resultCountTarget.textContent = ""
    this._hideConfirm()
    this.pendingUser = null

    // Show modal
    if (!this._modal) {
      const modalElement = this.element.querySelector("[data-smartabase-match-modal]")
      if (!modalElement) return
      this._modal = new bootstrap.Modal(modalElement)
    }
    this._modal.show()

    // Load users (once per modal open — cache on this.allUsers only if empty,
    // but always re-fetch to pick up new Smartabase registrations)
    this._loadUsers()
  }

  // ── Fetch Smartabase user list ─────────────────────────────────────────

  async _loadUsers() {
    this._showSpinner(true)
    this.resultsWrapperTarget.classList.add("d-none")

    try {
      const res = await fetch(this.fetchUrlValue, {
        headers: { "Accept": "application/json", "X-CSRF-Token": this._csrfToken() },
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      this.allUsers = this._sortUsers(await res.json())
      this._renderUsers(this.allUsers)
    } catch (err) {
      this.resultsTarget.innerHTML =
        `<tr><td colspan="4" class="text-danger">Failed to load users: ${err.message}</td></tr>`
      this.resultsWrapperTarget.classList.remove("d-none")
    } finally {
      this._showSpinner(false)
    }
  }

  // ── Filter ────────────────────────────────────────────────────────────────

  filter() {
    const q = this.searchTarget.value.toLowerCase().trim()
    if (!q) {
      this._renderUsers(this.allUsers)
      return
    }
    const filtered = this.allUsers.filter(u =>
      (u.lastName  || "").toLowerCase().includes(q) ||
      (u.firstName || "").toLowerCase().includes(q) ||
      (u.dob       || "").toLowerCase().includes(q)
    )
    this._renderUsers(filtered)
  }

  // ── Render results table ──────────────────────────────────────────────────

  _renderUsers(users) {
    this._hideConfirm()
    this.pendingUser = null

    if (!users.length) {
      this.resultsTarget.innerHTML =
        `<tr><td colspan="4" class="text-muted">No users match your search.</td></tr>`
      this.resultCountTarget.textContent = ""
    } else {
      this.resultsTarget.innerHTML = users.map(u => `
        <tr>
          <td>${this._esc(u.lastName)}</td>
          <td>${this._esc(u.firstName)}</td>
          <td>${this._esc(u.dob)}</td>
          <td class="text-end">
            <button class="btn btn-sm btn-outline-secondary"
                    data-action="click->smartabase-match#selectUser"
                    data-user-id="${this._esc(String(u.userId))}"
                    data-user-first="${this._esc(u.firstName)}"
                    data-user-last="${this._esc(u.lastName)}"
                    data-user-dob="${this._esc(u.dob)}">
              Select
            </button>
          </td>
        </tr>
      `).join("")
      this.resultCountTarget.textContent = `${users.length} user${users.length === 1 ? "" : "s"} shown`
    }

    this.resultsWrapperTarget.classList.remove("d-none")
  }

  // ── Select a user (show confirm panel) ───────────────────────────────────

  selectUser(event) {
    const btn = event.currentTarget
    this.pendingUser = {
      userId:    btn.dataset.userId,
      firstName: btn.dataset.userFirst,
      lastName:  btn.dataset.userLast,
      dob:       btn.dataset.userDob,
    }
    this._showInlineConfirm(btn.closest("tr"))
  }

  cancelConfirm() {
    this._hideConfirm()
    this.pendingUser = null
  }

  // ── Confirm & save the match ──────────────────────────────────────────────

  async confirmMatch() {
    if (!this.selectedAthlete || !this.pendingUser) return

    const btn = this.element.querySelector("[data-action*='confirmMatch']")
    if (btn) { btn.disabled = true; btn.textContent = "Saving…" }

    try {
      const res = await fetch(this.assignUrlValue, {
        method:  "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Accept":       "application/json",
          "X-CSRF-Token": this._csrfToken(),
        },
        body: JSON.stringify({
          athlete_id:       this.selectedAthlete.id,
          external_user_id: this.pendingUser.userId,
        }),
      })

      const data = await res.json()

      if (res.ok && data.success) {
        // Remove the athlete row from the unmatched table (affiliate group page)
        const row = document.getElementById(`unmatched-athlete-${this.selectedAthlete.id}`)
        if (row) row.remove()

        // Update the badge count (affiliate group page only)
        if (this.hasUnmatchedCountTarget) {
          const current = parseInt(this.unmatchedCountTarget.textContent, 10) || 0
          this.unmatchedCountTarget.textContent = Math.max(0, current - 1)
        }

        // Show success toast
        const toastMsg =
          `Matched "${this.selectedAthlete.name}" → ` +
          `"${this.pendingUser.lastName}, ${this.pendingUser.firstName}" ` +
          `(ID: ${this.pendingUser.userId})`
        this.toastBodyTarget.textContent = toastMsg
        const toastEl = this.element.querySelector("[data-smartabase-match-toast]")
        if (toastEl) new bootstrap.Toast(toastEl).show()

        // Close modal
        this._modal.hide()

        // On athlete page (no unmatched count target), reload so the EHR Match
        // option disappears from the dropdown now that external_id is set.
        if (!this.hasUnmatchedCountTarget) {
          setTimeout(() => window.location.reload(), 900)
        }
      } else {
        alert(`Error: ${data.error || "Unknown error"}`)
      }
    } catch (err) {
      alert(`Request failed: ${err.message}`)
    } finally {
      if (btn) { btn.disabled = false; btn.textContent = "Confirm Match" }
    }
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  _showSpinner(show) {
    this.spinnerTarget.classList.toggle("d-none", !show)
  }

  _hideConfirm() {
    if (this._confirmRow) {
      this._confirmRow.remove()
      this._confirmRow = null
    }
  }

  _showInlineConfirm(row) {
    this._hideConfirm()
    if (!row || !this.pendingUser || !this.selectedAthlete) return

    const confirmRow = document.createElement("tr")
    confirmRow.className = "table-warning"
    confirmRow.innerHTML = `
      <td colspan="4" class="p-3">
        <div class="fw-semibold mb-2">Confirm match?</div>
        <div class="small mb-2">
          <div><strong>Our system:</strong> ${this._esc(this.selectedAthlete.lastName)}, ${this._esc(this.selectedAthlete.firstName)}${this.selectedAthlete.dob ? ` <span class="text-muted">(DOB: ${this._esc(this.selectedAthlete.dob)})</span>` : ""}</div>
          <div><strong>EHR system:</strong> ${this._esc(this.pendingUser.lastName)}, ${this._esc(this.pendingUser.firstName)}${this.pendingUser.dob ? ` <span class="text-muted">(DOB: ${this._esc(this.pendingUser.dob)})</span>` : ""} <span class="text-muted">(User ID: <code>${this._esc(this.pendingUser.userId)}</code>)</span></div>
        </div>
        <div class="d-flex gap-2">
          <button class="btn btn-sm btn-secondary" data-action="click->smartabase-match#cancelConfirm">
            Cancel
          </button>
          <button class="btn btn-sm btn-success" data-action="click->smartabase-match#confirmMatch">
            Confirm Match
          </button>
        </div>
      </td>
    `

    row.insertAdjacentElement("afterend", confirmRow)
    this._confirmRow = confirmRow
    confirmRow.scrollIntoView({ behavior: "smooth", block: "nearest" })
  }

  _sortUsers(users) {
    return [...users].sort((a, b) => {
      const last = (a.lastName || "").localeCompare(b.lastName || "", undefined, { sensitivity: "base" })
      if (last !== 0) return last
      const first = (a.firstName || "").localeCompare(b.firstName || "", undefined, { sensitivity: "base" })
      if (first !== 0) return first
      return (a.dob || "").localeCompare(b.dob || "", undefined, { sensitivity: "base" })
    })
  }

  _csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  // Escape HTML to prevent XSS from Smartabase data
  _esc(str) {
    return String(str ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
  }
}
